import { Injectable, Inject } from '@angular/core';
import { v4 as uuidV4 } from 'uuid';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { forkJoin, from, Observable, of } from 'rxjs';
import { L10nTranslationService } from 'angular-l10n';
import { Cell, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { FilterView, ToolbarFilterOption } from '../../../filters-toolbar/filters-toolbar.model';
import { FiltersService } from '../../../filters-toolbar/filters.service';
import { FloorClass } from '../../../models/floor.class';
import { AbstractSpaceClass } from '../../../models/space/abstract-space.class';
import { AvailabilitySpaceClass } from '../../../models/space/availability-space.class';
import { NonRentableSpaceClass } from '../../../models/space/non-rentable-space.class';
import { TenantSpaceClass } from '../../../models/space/tenant-space.class';
import { UnaccountedSpaceClass } from '../../../models/space/unaccounted-space.class';
import { MY_FORMATS } from '../../../shared/constants/date.const';
import { DataObject } from '../../../models/data-object.models';
import { getTranslationInput, sanitizeValueOr, stringifyPostalCodeAddress } from '../../../shared/utils/functions';
import { MappedLov, PropertyData } from '../../../stacking-plan.model';
import { StackingPlanService } from '../../../stacking-plan.service';
import { ColorSchemeClass } from '../../../theme.const';
import { ElevatorBankProcessed, ElevatorTypes } from '../../floor-container/elevator-banks/elevator-banks.model';
import { FloorsElevators } from '../../stacking-plan.model';
import {
  AreaUrl,
  SpaceClassificationUrl,
} from '../../floor-container/floor/item/details-overlay/details-overlay.constants';
import { Settings, SettingsFilterView, SpaceCardDetails } from '../settings/settings.model';
import { SettingsService } from '../settings/settings.service';
import {
  BGColors,
  Colors,
  COLORS_BY_SCHEME,
  ColumnKeys,
  DEFAULT_MAX_NUMBERS_OF_CELLS,
  HUNDRED_PERCENT,
} from './export-to-excel.const';
import { ExportTemplate } from './export.model';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

type FloorWithElevatorData = FloorClass & { elevatorData?: ElevatorBankProcessed[]; maxArea?: number };
export enum FloorSizeStrategy {
  Building = 'building',
  Floor = 'floor',
}

@Injectable({ providedIn: 'root' })
export class NewExportToExcelService {
  private currentSettings: Settings;
  private filterOptions: { [templateKey: string]: ToolbarFilterOption[] } = {};
  private spaceDetailsOptions: SpaceCardDetails;
  private showElevators: boolean;
  private maxFloorStrategy: FloorSizeStrategy;
  private mappedUomLov: MappedLov;

  private static getSpaceAreaPercentage(space: AbstractSpaceClass, maxArea: number) {
    return maxArea > 0 ? Math.round(((space.getAreaSpace() || 0) * HUNDRED_PERCENT) / maxArea) : 1;
  }

  constructor(
    private filtersService: FiltersService,
    private stackingPlanService: StackingPlanService,
    private settingsService: SettingsService,
    private translationService: L10nTranslationService,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  generateWorkbook(
    floorElevatorData: FloorsElevators,
    selectedExportSettings: DataObject,
    localeId: number
  ): Observable<boolean> {
    this.currentSettings = this.settingsService.getSettings();
    this.currentSettings.colorScheme.none = ColorSchemeClass.Schema1;
    const workbook: Workbook = new Workbook();

    const { legends, showParameters, showOptions, sizeStrategy } = selectedExportSettings;

    this.spaceDetailsOptions = showParameters;
    this.showElevators = showOptions.showElevators;
    this.maxFloorStrategy = sizeStrategy;

    const areaUomLov$ = this.stackingPlanService.getLovValuesMap(AreaUrl);
    const spaceClassificationUomLov$ = this.stackingPlanService.getLovValuesMap(SpaceClassificationUrl);

    return forkJoin({ areaUomLov: areaUomLov$, spaceClassificationUomLov: spaceClassificationUomLov$ }).pipe(
      take(1),
      tap(
        ({ areaUomLov, spaceClassificationUomLov }) =>
          (this.mappedUomLov = { ...areaUomLov, ...spaceClassificationUomLov })
      ),
      switchMap(() => this.getSelectedTemplatesFilterOptions(legends, localeId)),
      switchMap(templateAndFilters => {
        templateAndFilters.forEach(([templateKey, filters]: [string, ToolbarFilterOption[]]) => {
          this.filterOptions[templateKey] = filters.map(filter => {
            const key: string = this.translationsForKeys(filter, 'key', templateKey);
            const label: string = this.translationsForKeys(filter, 'label');
            if (filter.spaceAreaUOM) {
              const value = this.mappedUomLov[filter.spaceAreaUOM];
              const spaceAreaUOM = value || filter.spaceAreaUOM;
              return { ...filter, key, spaceAreaUOM, label };
            } else {
              return { ...filter, key, label };
            }
          });
          this.generateTemplateWorksheet(workbook, floorElevatorData, templateKey as SettingsFilterView);
        });

        // Security: use uuidv4 as entropy source for the filename suffix.
        const uuidHex = uuidV4().replace(/-/g, '').slice(0, 8);
        const filename = `SP_${moment().format('MMDDYYYY')}_${(parseInt(uuidHex, 16) >>> 0) % 1000001}`.toLowerCase();
        return this.downloadWorkbook(workbook, `${filename}.xlsx`);
      }),
      catchError(err => {
        this.logger.error(`Error generating export workbook: ${err?.message ?? 'unknown error'}`);
        return of(false);
      })
    );
  }

  /**
   *
   * @param filter is @ToolbarFilterOption
   * @param translationLabel is label that needs translation.
   */
  private translationsForKeys(filter: ToolbarFilterOption, translationLabel: string, templateKey?: string) {
    let label: string = filter[translationLabel];
    if (label != null && this.translationService.has(label)) {
      let inputValue = null;
      if (templateKey === FilterView.LeaseYear) {
        inputValue = { year: getTranslationInput(label) };
      }
      return this.translationService.translate(label, inputValue);
    }
    return label;
  }

  /**
   * Depending on the user export template selections, request for each selected
   * template filter options. Map these options in an entry structure, using the
   * original selected template as key.
   *
   * @return tuple of template key and filter options
   * @example
   * ```
   * [
   *   "expiry",
   *   [
   *     {
   *       "id": 1,
   *       ...
   *       "spaces": [...]
   *     }
   *   ]
   * ]
   * ```
   */
  private getSelectedTemplatesFilterOptions(
    exportTemplate: ExportTemplate,
    localeId: number
  ): Observable<(string | ToolbarFilterOption[])[][]> {
    return forkJoin(
      Object.entries(exportTemplate)
        .filter(([_, isSelected]) => isSelected)
        .map(([templateKey]) =>
          this.stackingPlanService
            .getFilterData(
              (templateKey === SettingsFilterView.Expiry ? FilterView.Lease : templateKey) as FilterView,
              localeId,
              true
            )
            .pipe(map(filterOptions => [templateKey, filterOptions]))
        )
    ).pipe(take(1));
  }

  /**
   * Adds a worksheet to the created workbook using the template key as sheet
   * name
   */
  private generateTemplateWorksheet(
    workbook: Workbook,
    floorElevatorData: FloorsElevators,
    template: SettingsFilterView
  ) {
    const worksheet = this.initWorksheet(workbook, template);
    const { floorInfo, elevatorInfoProcessed } = floorElevatorData;
    const floorsWithElevators: FloorWithElevatorData[] = this.mapFloorsAndElevatorInfo(
      floorInfo.filter(floor => !floor.hideFloor),
      elevatorInfoProcessed,
      this.maxFloorStrategy
    );
    const numberOfElevatorBanks =
      this.showElevators && elevatorInfoProcessed != null && !!elevatorInfoProcessed.length
        ? elevatorInfoProcessed[0].length
        : 0;
    this.defineColumnHeaders(worksheet, this.stackingPlanService.propertyData, numberOfElevatorBanks);
    floorsWithElevators.forEach(this.generateFloorRow.bind(this, worksheet));
    this.generateFillerCell(worksheet);
    this.styleHeaderRow(worksheet);
    this.generateFiltersLabels(worksheet, template);
  }

  private initWorksheet(workbook: Workbook, template: SettingsFilterView): Worksheet {
    const sheetTitle = this.translationService.translate(`exportAndSettings.${template}`);
    const worksheet: Worksheet = workbook.addWorksheet(sheetTitle, {
      properties: { defaultColWidth: 22 },
    });
    /*
     * NOTE: custom worksheet attribute to store current template view this
     *  change got applied as the sheet name was being used as key to get data
     *  from different maps. As per this change, the sheet title uses a
     *  translated value, meaning that it was not able to be used as key anymore
     *  Current Usages:
     *   - styleTenantSpaceCell
     *   - generateFiltersLabels
     */
    worksheet.templateKey = template;
    return worksheet;
  }

  private generateFloorRow(worksheet: Worksheet, floor: FloorWithElevatorData, index: number) {
    const floorRow = worksheet.getRow(index + 2);
    const floorNumberCell = floorRow.getCell(ColumnKeys.FloorNumber);
    const floorSpaceCell = floorRow.getCell(ColumnKeys.FloorSpace);
    floorNumberCell.value = floor.toString();
    floorSpaceCell.value = floor.floorArea || '';

    this.styleFloorRow(floorRow);
    this.styleFloorColumn(floorNumberCell);
    this.styleFloorColumn(floorSpaceCell, false);

    this.generateSpacesCells(floorRow, floor);
    this.generateElevatorBankCells(floorRow, floor.elevatorData);
  }

  private downloadWorkbook(workbook: Workbook, filename: string): Observable<boolean> {
    return from(workbook.xlsx.writeBuffer()).pipe(
      tap((data: any) => saveAs(new Blob([data]), filename)),
      map(() => true),
      catchError(err => {
        this.logger.error(`Error downloading workbook: ${err?.message ?? 'unknown error'}`);
        return of(false);
      })
    );
  }

  private generateSpacesCells(floorRow: Row, floor: FloorWithElevatorData) {
    const { spaces, floorNumber, maxArea } = floor;
    if (spaces != null && !!spaces.length) {
      let nextCell = floorRow.cellCount + 1;
      const normalSizedSpaces = spaces.filter(space => {
        if (!(space instanceof UnaccountedSpaceClass)) {
          const cellsToOccupy = NewExportToExcelService.getSpaceAreaPercentage(space, maxArea);
          return cellsToOccupy >= 5;
        }
        return false;
      });
      normalSizedSpaces.forEach(space => {
        nextCell = this.generateSpaceCell(floorRow, maxArea, nextCell, space, floorNumber);
      });
      // get all the small (less than 5 cells) spaces
      if (normalSizedSpaces.length !== spaces.length) {
        const normalSizeSpacesSum = normalSizedSpaces.reduce((acc, space) => acc + space.getAreaSpace(), 0);
        let smallSizedMaxArea = maxArea;
        let maxCellsSpace: number;
        if (normalSizeSpacesSum > 0) {
          smallSizedMaxArea = maxArea - normalSizeSpacesSum;
          maxCellsSpace = ((maxArea - normalSizeSpacesSum) * HUNDRED_PERCENT) / maxArea;
        }
        const smallSizedSpaces = spaces.filter(space => {
          const cellsToOccupy = NewExportToExcelService.getSpaceAreaPercentage(space, maxArea);
          return cellsToOccupy < 5;
        });
        smallSizedSpaces.forEach(space => {
          nextCell = this.generateSpaceCell(floorRow, smallSizedMaxArea, nextCell, space, floorNumber, maxCellsSpace);
        });
      }
    }
  }

  private defineColumnHeaders(worksheet, propertyData: PropertyData, numberOfElevatorBanks: number) {
    const elevatorColumns = Array(numberOfElevatorBanks).fill({ width: 2 });
    worksheet.columns = [
      ...elevatorColumns,
      {
        header: this.translationService.translate('exportToExcel.floorNumberHeader'),
        key: ColumnKeys.FloorNumber,
        width: 18,
      },
      {
        header: this.translationService.translate('exportToExcel.floorSizeHeader'),
        key: ColumnKeys.FloorSpace,
        width: 20,
      },
      {
        header: `${this.generateReportTitle(propertyData)}`,
        key: ColumnKeys.Spaces,
        width: 2,
      },
    ];
  }

  private generateSpaceCell(
    floorRow: Row,
    maxArea: number,
    currentCellPosition: number,
    space: AbstractSpaceClass,
    floorNumber: string,
    maxCellsToUse = DEFAULT_MAX_NUMBERS_OF_CELLS
  ): number {
    let spacePercentage = NewExportToExcelService.getSpaceAreaPercentage(space, maxArea);
    const cellsToOccupy = Math.floor(
      maxCellsToUse === DEFAULT_MAX_NUMBERS_OF_CELLS
        ? spacePercentage
        : (spacePercentage * maxCellsToUse) / HUNDRED_PERCENT
    );

    const firstCell: Cell = floorRow.getCell(currentCellPosition);
    let lastCellToMerge = firstCell;
    if (cellsToOccupy > 1) {
      const worksheet = floorRow.worksheet;
      lastCellToMerge = floorRow.getCell(currentCellPosition + cellsToOccupy - 1);
      worksheet.mergeCells(`${firstCell.address}:${lastCellToMerge.address}`);
    }
    firstCell.value = space
      .toString({ detailsToDisplay: this.spaceDetailsOptions, mappedUomLov: this.mappedUomLov })
      .trim();
    this.styleSpaceCell(firstCell, space, floorNumber);
    return lastCellToMerge.col + 1;
  }

  /**
   * A filler cell is the used to fill the rest of the floors, using the max
   * column count from the worksheet. The filler cell is the merge of the first
   * cell after the last existing space cell and the last column of the row with
   * more cells.
   *
   * For the first row, the header row, it uses the existing 'spaces' header as
   * the cell to begin the merge.
   */
  private generateFillerCell(worksheet: Worksheet) {
    const maxNumberOfColumns = worksheet.columnCount;
    const firstSpaceColumn = worksheet.getColumn(ColumnKeys.Spaces);
    const hundredPercentColumnNumber = firstSpaceColumn.number + 99;
    const lastSpaceColumn = worksheet.getColumn(maxNumberOfColumns);

    for (let i = firstSpaceColumn.number; i <= lastSpaceColumn.number; i++) {
      worksheet.getColumn(i).width = 2;
    }

    worksheet.eachRow((row: Row) => {
      const isHeaderRow = row.number === 1;
      const rowNumberOfCells = row.cellCount;
      const lastFillerCell = row.getCell(hundredPercentColumnNumber);
      // do not fill first row (header)
      const firstCellNumber = !isHeaderRow ? rowNumberOfCells + 1 : rowNumberOfCells;
      const firstFillerCell = row.getCell(firstCellNumber);
      if (rowNumberOfCells < lastFillerCell.col) {
        worksheet.mergeCells(`${firstFillerCell.address}:${lastFillerCell.address}`);
        firstFillerCell.value = isHeaderRow ? firstFillerCell.value : '';
        this.styleSpaceCell(firstFillerCell);
      }
    });
  }

  private mapFloorsAndElevatorInfo(
    floors: FloorClass[],
    elevatorBanks: ElevatorBankProcessed[][],
    maxFloorSizeStrategy: FloorSizeStrategy
  ): FloorWithElevatorData[] {
    // calculate greatest floor in the building
    let maxBuildingFloorSize = floors.reduce((acc, floor) => {
      // const occupiedArea = ; // sum all existing spaces
      const maxFloorArea =
        floor.getFloorSpaceAreaSum() > (floor.floorArea || 0) ? floor.getFloorSpaceAreaSum() : floor.floorArea || 0;
      return acc > maxFloorArea ? acc : maxFloorArea;
    }, 0);
    // calculate greatest floor in the building
    return floors.map<FloorWithElevatorData>((floor, index) => {
      const clonedFloor = floor.clone() as FloorWithElevatorData;
      if (this.showElevators && elevatorBanks != null && !!elevatorBanks.length) {
        clonedFloor.elevatorData = elevatorBanks[index];
      }
      // US approach
      if (maxFloorSizeStrategy === FloorSizeStrategy.Floor) {
        // get max floor size
        clonedFloor.maxArea =
          clonedFloor.getFloorSpaceAreaSum() > (floor.floorArea || 0)
            ? clonedFloor.getFloorSpaceAreaSum()
            : clonedFloor.floorArea;
        // get max floor size
      } else {
        clonedFloor.maxArea = maxBuildingFloorSize;
      }
      return clonedFloor;
    });
  }

  private styleFloorRow(floorRow: Row) {
    let defaultRowHeight = 20;
    const defaultDisplayedParams = 3;
    const detailsOptionsValues = Object.values(this.spaceDetailsOptions);
    if (detailsOptionsValues.some(isFieldRequired => isFieldRequired)) {
      const numberOfDisplayedParams = defaultDisplayedParams + detailsOptionsValues.filter(Boolean).length;
      defaultRowHeight += numberOfDisplayedParams * 8;
    }
    floorRow.height = defaultRowHeight;
    floorRow.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    floorRow.alignment = { vertical: 'top' };
    floorRow.font = { name: 'Arial' };
  }

  private styleSpaceCell(spaceCell: Cell, space?: AbstractSpaceClass, floorNumber?: string) {
    spaceCell.alignment = { wrapText: true, indent: 1, vertical: 'top' };
    spaceCell.border = {
      right: { style: 'thin', color: { argb: Colors.White } },
      bottom: { style: 'thin', color: { argb: Colors.White } },
    };
    if ((space != null && space instanceof AvailabilitySpaceClass) || space instanceof NonRentableSpaceClass) {
      spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.DefaultBG } };
    } else if (space != null && space instanceof TenantSpaceClass) {
      this.styleTenantSpaceCell(spaceCell, space, floorNumber);
    } else {
      spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.FillerBG } };
    }
  }

  /**
   * depending on the selected template (stored and used as the worksheet name)
   * and the current user settings theme, color to the tenant space cell will
   * be applied
   *
   * Notice that a custom filter options array is passed to
   * {@link getItemSchemaClass} so the service uses the template filter values
   * instead of the current view filters.
   */
  private styleTenantSpaceCell(spaceCell: Cell, space: TenantSpaceClass, floorNumber: string) {
    const exportTemplate: SettingsFilterView = spaceCell.worksheet.templateKey;
    const { class: klass = '' } = this.filtersService.getItemSchemaClass(
      space,
      floorNumber,
      this.filterOptions[exportTemplate]
    );
    const viewColorScheme = this.currentSettings.colorScheme[exportTemplate];
    const colorPalette = COLORS_BY_SCHEME[viewColorScheme || 'Default'][klass];
    const fontColor = klass === 'expire-na' ? Colors.DefaultText : Colors.White;
    spaceCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorPalette } };
    spaceCell.font = { ...(spaceCell.font != null ? spaceCell.font : {}), color: { argb: fontColor } };
  }

  private styleFloorColumn(floorCell: Cell, hasRightBorder = true) {
    floorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.FloorColumn } };
    floorCell.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    if (hasRightBorder) {
      floorCell.border.right = { style: 'thin', color: { argb: Colors.White } };
    }
    floorCell.alignment = { horizontal: 'center', vertical: 'middle' };
    floorCell.font = {
      ...(floorCell.font != null ? floorCell.font : {}),
      bold: true,
      color: { argb: Colors.DefaultText },
    };
  }

  private styleHeaderRow(worksheet: Worksheet) {
    const headerRow: Row = worksheet.getRow(1);
    headerRow.height = 40;
    headerRow.border = null;
    headerRow.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };
    headerRow.font = {
      name: 'Arial',
      size: 14,
      bold: true,
      color: { argb: Colors.White },
    };
    const floorDataColumnFillConfig = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BGColors.FloorColumnHeader },
    };
    const floorNumberCell = headerRow.getCell(ColumnKeys.FloorNumber);
    const firstHeaderCell = headerRow.getCell(1);
    if (floorNumberCell !== firstHeaderCell) {
      /*
       * if firstHeaderCell is different to floorNumberCell, it means that an
       * elevator banks exist. Therefore, merge and format Elevator Bank header
       * cells.
       */
      worksheet.mergeCells(`${firstHeaderCell.address}:${headerRow.getCell(floorNumberCell.col - 1).address}`);
      firstHeaderCell.border = { bottom: { style: 'thin', color: { argb: Colors.White } } };
    }
    headerRow.getCell(ColumnKeys.FloorSpace).fill = floorDataColumnFillConfig;

    floorNumberCell.fill = floorDataColumnFillConfig;
    const spaceHeader = headerRow.getCell(headerRow.cellCount);
    spaceHeader.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: BGColors.SpacesHeader },
    };
    spaceHeader.alignment = { vertical: 'top', indent: 1, wrapText: true };
  }

  private generateFiltersLabels(worksheet: Worksheet, template: SettingsFilterView) {
    let firstFiltersRowNumber = worksheet.lastRow.number + 2;
    const viewColorScheme = this.currentSettings.colorScheme[template];
    const currentSheetColorScheme = COLORS_BY_SCHEME[viewColorScheme || 'Default'];
    const filterLabelsAndClasses = this.filterOptions[worksheet.templateKey].map(({ class: klass, label, key }) => [
      currentSheetColorScheme[klass],
      label || key,
    ]);
    // use first row/cell to display the filters template name
    const filterHeaderRow: Row = worksheet.getRow(firstFiltersRowNumber++);
    const templateHeaderCell = filterHeaderRow.getCell(ColumnKeys.FloorNumber);
    templateHeaderCell.value = worksheet.name.toUpperCase();
    filterHeaderRow.height = 22;
    filterHeaderRow.alignment = { vertical: 'middle', indent: 1 };

    filterLabelsAndClasses.forEach(([colorArgb, label], index, arr) => {
      const filterRow: Row = worksheet.getRow(firstFiltersRowNumber++);
      const colorCell = filterRow.getCell(ColumnKeys.FloorNumber);
      const labelCell = filterRow.getCell(ColumnKeys.FloorSpace);

      // Rows and Cells values
      colorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colorArgb } };
      labelCell.value = label;

      // Rows and Cells styles
      filterRow.height = 22;
      filterRow.alignment = { vertical: 'middle', indent: 1 };
      const borderStyle = { style: 'thin', color: { argb: Colors.DefaultText } };
      colorCell.border = {};
      labelCell.border = {};
      if (index === 0) {
        colorCell.border.top = borderStyle;
        labelCell.border.top = borderStyle;
      }
      if (index >= 0) {
        colorCell.border.left = borderStyle;
        labelCell.border.right = borderStyle;
      }
      if (index === arr.length - 1) {
        colorCell.border.bottom = borderStyle;
        labelCell.border.bottom = borderStyle;
      }
    });
  }

  private generateElevatorBankCells(floorRow: Row, elevatorData: ElevatorBankProcessed[]) {
    (elevatorData || []).forEach((elevator, index) => {
      const elevatorCell = floorRow.getCell(index + 1);
      elevatorCell.border = {
        bottom: { style: 'thin', color: { argb: Colors.White } },
        right: { style: 'thin', color: { argb: Colors.White } },
      };
      if (elevator.elevatorType !== ElevatorTypes.ThinLine && elevator.elevatorType !== ElevatorTypes.Empty) {
        elevatorCell.border.bottom = {};
        elevatorCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BGColors.ElevatorBank } };
      }
    });
  }

  private generateReportTitle(propertyData: PropertyData): string {
    const propertyName = sanitizeValueOr('-', propertyData.propertyName);
    const propertyAddress = stringifyPostalCodeAddress(propertyData.postalAddress);
    return `${propertyName} - ${propertyAddress}\n${moment().format(MY_FORMATS)}`;
  }
}
